/**
 * 🎨 Theme Configuration
 * 앱 전체에서 사용하는 테마 스타일 정의
 * 
 * 사용법:
 * import { THEME_STYLES, getThemeByColor, getThemeForDepartment, DEFAULT_THEME } from '../constants/theme';
 */

export const DEFAULT_THEME_KEY = "olive";
// 기존 코드 호환용: class 기반 기본값은 유지
export const DEFAULT_COLOR = "bg-[#354F38]";

const THEME_PRESETS = {
    olive: {
        key: "olive",
        label: "올리브",
        primaryHex: "#354F38",
        bg: "bg-[#354F38]",
        text: "text-[#354F38]",
        border: "border-[#354F38]",
        shadow: "shadow-[#354F38]/30",
        hoverBg: "hover:bg-[#354F38]/5",
        lightBg: "bg-[#354F38]/10"
    },
    indigo: {
        key: "indigo",
        label: "인디고",
        primaryHex: "#4F46E5",
        bg: "bg-indigo-600",
        text: "text-indigo-600",
        border: "border-indigo-600",
        shadow: "shadow-indigo-600/30",
        hoverBg: "hover:bg-indigo-600/5",
        lightBg: "bg-indigo-600/10"
    },
    teal: {
        key: "teal",
        label: "틸",
        primaryHex: "#0F766E",
        bg: "bg-teal-700",
        text: "text-teal-700",
        border: "border-teal-700",
        shadow: "shadow-teal-700/30",
        hoverBg: "hover:bg-teal-700/5",
        lightBg: "bg-teal-700/10"
    },
    emerald: {
        key: "emerald",
        label: "에메랄드",
        primaryHex: "#047857",
        bg: "bg-emerald-700",
        text: "text-emerald-700",
        border: "border-emerald-700",
        shadow: "shadow-emerald-700/30",
        hoverBg: "hover:bg-emerald-700/5",
        lightBg: "bg-emerald-700/10"
    },
    amber: {
        key: "amber",
        label: "앰버",
        primaryHex: "#B45309",
        bg: "bg-amber-700",
        text: "text-amber-700",
        border: "border-amber-700",
        shadow: "shadow-amber-700/30",
        hoverBg: "hover:bg-amber-700/5",
        lightBg: "bg-amber-700/10"
    },
    rose: {
        key: "rose",
        label: "로즈",
        primaryHex: "#BE123C",
        bg: "bg-rose-700",
        text: "text-rose-700",
        border: "border-rose-700",
        shadow: "shadow-rose-700/30",
        hoverBg: "hover:bg-rose-700/5",
        lightBg: "bg-rose-700/10"
    },
    slate: {
        key: "slate",
        label: "슬레이트",
        primaryHex: "#475569",
        bg: "bg-slate-600",
        text: "text-slate-600",
        border: "border-slate-600",
        shadow: "shadow-slate-600/30",
        hoverBg: "hover:bg-slate-600/5",
        lightBg: "bg-slate-600/10"
    }
};

const LEGACY_COLOR_TO_THEME_KEY = {
    "bg-[#354F38]": "olive",
    "bg-indigo-500": "indigo",
    "bg-indigo-600": "indigo",
    "bg-teal-500": "teal",
    "bg-teal-700": "teal",
    "bg-lime-600": "emerald",
    "bg-emerald-700": "emerald",
    "bg-orange-500": "amber",
    "bg-yellow-500": "amber",
    "bg-amber-700": "amber",
    "bg-pink-400": "rose",
    "bg-purple-600": "indigo",
    "bg-blue-500": "indigo",
    "bg-rose-500": "rose",
    "bg-rose-700": "rose",
    "bg-slate-500": "slate",
    "bg-slate-600": "slate"
};

// 모든 테마 스타일 정의 (token-key 기반)
export const THEME_STYLES = Object.fromEntries(
    Object.entries(THEME_PRESETS).map(([key, preset]) => [key, {
        key: preset.key,
        primaryHex: preset.primaryHex,
        bg: preset.bg,
        text: preset.text,
        border: preset.border,
        shadow: preset.shadow,
        hoverBg: preset.hoverBg,
        lightBg: preset.lightBg
    }])
);

// 기본 테마
export const DEFAULT_THEME = THEME_STYLES[DEFAULT_THEME_KEY];

const THEME_KEY_SET = new Set(Object.keys(THEME_PRESETS));

export const normalizeThemeKey = (value) => {
    if (typeof value !== "string" || !value.trim()) return DEFAULT_THEME_KEY;
    if (THEME_KEY_SET.has(value)) return value;
    return LEGACY_COLOR_TO_THEME_KEY[value] || DEFAULT_THEME_KEY;
};

export const resolveThemeColorClass = (value) => {
    const key = normalizeThemeKey(value);
    return THEME_PRESETS[key].bg;
};

/**
 * 색상 키로 테마 가져오기
 * @param {string} colorKey - 토큰 키(권장) 또는 legacy color class
 * @returns {object} 테마 스타일 객체
 */
export const getThemeByColor = (colorKey) => {
    const key = normalizeThemeKey(colorKey);
    return THEME_STYLES[key] || DEFAULT_THEME;
};

/**
 * 부서 및 사용자 프로필 기반으로 테마 가져오기
 * @param {string} selectedDepartment - 선택된 부서 ("All" 또는 부서명)
 * @param {object} userProfile - 사용자 프로필 객체
 * @returns {object} 테마 스타일 객체
 */
export const getThemeForDepartment = (selectedDepartment, userProfile) => {
    if (!userProfile) return DEFAULT_THEME;

    // 1. 특정 부서 색상
    if (selectedDepartment !== "All") {
        const deptThemeKey =
            userProfile.departmentThemeKeys?.[selectedDepartment] ||
            normalizeThemeKey(userProfile.departmentColors?.[selectedDepartment]);
        return getThemeByColor(deptThemeKey);
    }

    // 2. 기본 부서 색상 (All이거나 주 부서일 때)
    if (selectedDepartment === "All" || selectedDepartment === userProfile.deptName) {
        const primaryThemeKey = userProfile.deptThemeKey || normalizeThemeKey(userProfile.deptColor || DEFAULT_COLOR);
        return getThemeByColor(primaryThemeKey);
    }

    // 3. 기본값
    return DEFAULT_THEME;
};

const hexToRgb = (hex) => {
    const cleaned = hex.replace("#", "");
    const normalized = cleaned.length === 3
        ? cleaned.split("").map((ch) => ch + ch).join("")
        : cleaned;
    const intVal = parseInt(normalized, 16);
    return {
        r: (intVal >> 16) & 255,
        g: (intVal >> 8) & 255,
        b: intVal & 255
    };
};

const toRelativeLuminance = ({ r, g, b }) => {
    const toLinear = (n) => {
        const srgb = n / 255;
        return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
    };

    const rr = toLinear(r);
    const gg = toLinear(g);
    const bb = toLinear(b);
    return 0.2126 * rr + 0.7152 * gg + 0.0722 * bb;
};

const getContrastRatio = (hexA, hexB) => {
    const lumA = toRelativeLuminance(hexToRgb(hexA));
    const lumB = toRelativeLuminance(hexToRgb(hexB));
    const lighter = Math.max(lumA, lumB);
    const darker = Math.min(lumA, lumB);
    return (lighter + 0.05) / (darker + 0.05);
};

// 현재 UI는 primary 배경 + white text 패턴을 많이 사용하므로 white 대비 기준으로 검사
export const isThemeContrastAccessible = (themeKey, minRatio = 4.5) => {
    const key = normalizeThemeKey(themeKey);
    const preset = THEME_PRESETS[key];
    if (!preset) return false;
    return getContrastRatio(preset.primaryHex, "#FFFFFF") >= minRatio;
};

export const THEME_PRESET_OPTIONS = Object.values(THEME_PRESETS).map((preset) => ({
    key: preset.key,
    label: preset.label,
    swatchClass: preset.bg,
    primaryHex: preset.primaryHex
}));

// 토큰 키 목록 (설정에서 선택 가능한 색상들)
export const AVAILABLE_COLORS = Object.keys(THEME_STYLES);
